import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import css from '@styled-system/css';
import shouldForwardProp from '@styled-system/should-forward-prop';
import { variant } from 'styled-system';

import { ConditionalRender } from '../../internal/components';
import sx from '../../internal/styles/sx';
import { BaseFlex, Box } from '../../layouts';
import BackDrop from '../BackDrop';
import Portal from '../Portal';
import { SIZES_PX } from './constants';
import { ModalProps } from './types';

export const ModalWrapper = styled(BaseFlex)<
  Pick<ModalProps, 'transitionDuration' | 'state' | 'size'>
>(
  css({
    position: 'fixed',
    zIndex: 'modal',
    overflowY: 'auto',
    maxHeight: '100%',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  }),
  ({ transitionDuration }) => ({
    transitionDuration: `${transitionDuration}ms`,
  }),
  variant({
    prop: 'state',
    variants: {
      opening: {
        opacity: 0,
        transitionTimingFunction: 'ease-out',
      },
      opened: {
        opacity: 1,
        transitionTimingFunction: 'ease-out',
      },
      closing: {
        opacity: 0,
        transitionTimingFunction: 'ease-in',
      },
    },
  })
);

export const ModalBox = styled(Box, { shouldForwardProp })<
  Pick<ModalProps, 'size' | 'sx'>
>(
  css({
    position: 'relative',
    width: '100%',
    fontFamily: 'body',
    boxShadow: '20',
    backgroundColor: 'white',
    flexDirection: 'column',
    borderRadius: 2,
    overflow: 'hidden',
    margin: 'auto',
  }),
  variant({
    prop: 'size',
    variants: {
      small: {
        width: `${SIZES_PX['small']}px`,
      },
      medium: {
        width: `${SIZES_PX['medium']}px`,
      },
      large: {
        width: `${SIZES_PX['large']}px`,
      },
      full: {
        width: '100vw',
      },
    },
  }),
  sx
);

const handleStopPropagation: React.MouseEventHandler = (event) => {
  event.stopPropagation();
};

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      children,
      state,
      onClose,
      rootElement,
      backDrop = <BackDrop open={state !== 'closed'} onClick={onClose} />,
      size,
      transitionDuration = 200,
      sx,
      ...rest
    },
    ref
  ) => {
    return (
      <Portal rootElement={rootElement} {...rest}>
        <div {...rest}>
          <ConditionalRender condition={state !== 'closed'}>
            {backDrop}
            <ModalWrapper
              state={state}
              transitionDuration={transitionDuration}
              role="dialog"
              aria-modal="true"
            >
              <ModalBox
                onClick={handleStopPropagation}
                ref={ref}
                sx={sx}
                size={size}
              >
                {children}
              </ModalBox>
            </ModalWrapper>
          </ConditionalRender>
        </div>
      </Portal>
    );
  }
);
